import { useEffect, useMemo, useRef, useState } from 'react'

import { useTelemetrySource } from '../../lib/api/source.tsx'
import type { LogHistoryQuery } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { formatInt } from '../../lib/format.ts'
import { agentHref, logsHref } from '../../lib/logs/investigation.ts'
import type { LogInvestigation } from '../../lib/logs/investigation.ts'
import { EVENT_MODULES, moduleLabel } from '../../lib/logs/semantics.ts'
import { navigate } from '../../lib/router.tsx'
import type { LogRow } from '../../lib/sse/types.ts'
import { Button } from '../primitives/Button.tsx'
import { SelectField, TextField } from '../primitives/Field.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { InvestigationBanner } from './InvestigationBanner.tsx'
import { LogTable, OrderAxisNote } from './LogTable.tsx'

const PAGE_SIZE = 50

interface Draft {
  eventType: string
  eventAction: string
  userName: string
  eventModule: string
  processName: string
  serviceName: string
  fileExtension: string
  dnsQuestion: string
  source: string
  nativeId: string
  processEntityId: string
  relatedIp: string
  relatedUser: string
  relatedHash: string
  from: string
  to: string
}

/** As opções do select de módulo, com o "todos" vazio à frente. */
const MODULE_OPTIONS = [
  { value: '', label: 'Todos os módulos' },
  ...EVENT_MODULES.map((m) => ({ value: m, label: moduleLabel(m) ?? m })),
]

/** datetime-local (YYYY-MM-DDTHH:mm) tratado como UTC — o console é todo UTC. */
function toUtcIso(local: string): string | undefined {
  return local === '' ? undefined : `${local}:00Z`
}

/** ISO (com offset/ms) → o valor de um `datetime-local`, em UTC e por minuto. */
function isoToLocalInput(iso: string | undefined): string {
  if (iso === undefined) return ''
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return ''
  const d = new Date(ms)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

function draftFrom(inv: LogInvestigation): Draft {
  return {
    eventType: inv.eventType ?? '',
    eventAction: inv.eventAction ?? '',
    userName: inv.userName ?? '',
    eventModule: inv.eventModule ?? '',
    processName: inv.processName ?? '',
    serviceName: inv.serviceName ?? '',
    fileExtension: inv.fileExtension ?? '',
    dnsQuestion: inv.dnsQuestion ?? '',
    source: inv.source ?? '',
    nativeId: inv.nativeId ?? '',
    processEntityId: inv.processEntityId ?? '',
    relatedIp: inv.relatedIp ?? '',
    relatedUser: inv.relatedUser ?? '',
    relatedHash: inv.relatedHash ?? '',
    from: isoToLocalInput(inv.from),
    to: isoToLocalInput(inv.to),
  }
}

const clean = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * O histórico paginado, agora dirigido pela URL (§5, §7).
 *
 * O recorte inteiro — escopo, tipo, ação, usuário e os pivôs de fan-out — mora
 * na query string, para que "investigar este processo" e "colar o link num
 * ticket" sejam a mesma coisa (§7, §9). Buscar não guarda estado: NAVEGA, e a
 * URL vira a fonte da verdade que a listagem relê.
 *
 * É sempre completo, em qualquer topologia — lê o banco compartilhado —, e por
 * isso é a resposta certa quando o tail está em cobertura parcial. Paginação por
 * cursor, sem total (§5.2): um "carregar mais antigos", nunca uma régua numerada.
 *
 * As regras de filtro que RECUSAM (400) são prevenidas aqui (§5.1, §13): tipo,
 * ação e usuário exigem empresa ou agente; canal e identificador de origem
 * exigem agente; período exige empresa, agente, um processo OU uma consulta DNS.
 * Os pivôs de fan-out (processo, IP, usuário, hash, DNS) não exigem escopo. O
 * 400 é a rede de segurança, não o mecanismo.
 */
export function LogHistory({
  scopeEndpointId,
  investigation,
}: {
  scopeEndpointId?: string
  investigation: LogInvestigation
}) {
  const source = useTelemetrySource()
  // Na rota de cliente a empresa está SEMPRE no escopo: vem do token (§6.3).
  const hasScope = source.alwaysScoped || Boolean(investigation.tenantId ?? scopeEndpointId)
  const hasEndpoint = Boolean(scopeEndpointId)

  const [draft, setDraft] = useState<Draft>(() => draftFrom(investigation))

  const committed = useMemo<LogHistoryQuery>(
    () => ({
      endpointId: scopeEndpointId,
      tenantId: investigation.tenantId,
      eventType: investigation.eventType,
      eventAction: investigation.eventAction,
      userName: investigation.userName,
      eventModule: investigation.eventModule,
      processName: investigation.processName,
      serviceName: investigation.serviceName,
      fileExtension: investigation.fileExtension,
      dnsQuestion: investigation.dnsQuestion,
      source: investigation.source,
      nativeId: investigation.nativeId,
      processEntityId: investigation.processEntityId,
      relatedIp: investigation.relatedIp,
      relatedUser: investigation.relatedUser,
      relatedHash: investigation.relatedHash,
      from: investigation.from,
      to: investigation.to,
    }),
    [scopeEndpointId, investigation],
  )
  // A URL é a identidade da consulta; uma string estável dela é o gatilho de
  // refetch, e o que ressincroniza o rascunho quando um pivô é adicionado ou
  // limpo por fora do formulário.
  const committedKey = JSON.stringify(committed)

  const [rows, setRows] = useState<LogRow[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [order, setOrder] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)
  const cursorRef = useRef<string | null>(null)

  // Ressincroniza o rascunho quando a URL muda por fora do formulário (um pivô
  // adicionado/limpo). `committedKey` resume o objeto `investigation`.
  useEffect(() => setDraft(draftFrom(investigation)), [committedKey])

  useEffect(() => {
    let cancelled = false
    cursorRef.current = null
    setRows([])
    setHasMore(false)
    setOrder('')
    setError(null)
    setLoading(true)

    source
      .logs({ ...committed, cursor: null, pageSize: PAGE_SIZE })
      .then((page) => {
        if (cancelled) return
        cursorRef.current = page.next_cursor
        setRows(page.items)
        setHasMore(page.has_more)
        setOrder(page.order)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // Refaz a primeira página a cada novo recorte (committedKey resume
    // `committed`) e a cada retry manual (nonce).
  }, [source, committedKey, nonce])

  const loadOlder = (): void => {
    if (loading) return
    setLoading(true)
    setError(null)
    source
      .logs({ ...committed, cursor: cursorRef.current, pageSize: PAGE_SIZE })
      .then((page) => {
        // Devolva `next_cursor` sem alterá-lo (§5.2).
        cursorRef.current = page.next_cursor
        setRows((prev) => [...prev, ...page.items])
        setHasMore(page.has_more)
        setOrder(page.order)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        setError(cause)
        setLoading(false)
      })
  }

  // Processo e DNS têm índice próprio e sustentam período sozinhos (§12.4).
  const draftHasTimeScope =
    hasScope ||
    clean(draft.processEntityId) !== undefined ||
    clean(draft.dnsQuestion) !== undefined

  const search = (): void => {
    const next: LogInvestigation = {
      // O escopo de empresa é escolhido fora do formulário (no seletor); o de
      // agente é o segmento de rota. Preserve-os.
      tenantId: investigation.tenantId,
      eventType: clean(draft.eventType),
      eventAction: clean(draft.eventAction),
      userName: clean(draft.userName),
      // Os quatro primeiros exigem escopo — quando não há, o campo está
      // desabilitado e o rascunho fica vazio, então `clean` já os descarta. O
      // `code` 400 é a rede. DNS é pivô de frota e vai sempre (§12.4).
      eventModule: clean(draft.eventModule),
      processName: clean(draft.processName),
      serviceName: clean(draft.serviceName),
      // Com ou sem ponto: o servidor normaliza, o cliente passa como veio.
      fileExtension: clean(draft.fileExtension),
      dnsQuestion: clean(draft.dnsQuestion),
      source: hasEndpoint ? clean(draft.source) : undefined,
      nativeId: hasEndpoint ? clean(draft.nativeId) : undefined,
      processEntityId: clean(draft.processEntityId),
      relatedIp: clean(draft.relatedIp),
      relatedUser: clean(draft.relatedUser),
      relatedHash: clean(draft.relatedHash),
      from: draftHasTimeScope ? toUtcIso(draft.from) : undefined,
      to: draftHasTimeScope ? toUtcIso(draft.to) : undefined,
    }
    navigate(scopeEndpointId === undefined ? logsHref(next) : agentHref(scopeEndpointId, next))
  }

  const cursorInvalid = error instanceof AthenaError && error.code === 'logs_cursor_invalid'

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 empty:hidden">
        <InvestigationBanner inv={investigation} endpointId={scopeEndpointId} />
      </div>

      {!hasScope && (
        <div className="shrink-0 px-3 pt-2">
          <Notice tone="neutral">
            Escolha uma empresa ou um agente para filtrar por período, tipo, ação ou usuário.
          </Notice>
        </div>
      )}

      <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-line-subtle px-3 py-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <TextField
          label="Tipo de evento"
          value={draft.eventType}
          onValueChange={(v) => setDraft((d) => ({ ...d, eventType: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'ex.: process_create' : 'exige empresa ou agente'}
          maxLength={100}
          mono
        />
        <TextField
          label="Ação"
          value={draft.eventAction}
          onValueChange={(v) => setDraft((d) => ({ ...d, eventAction: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'ex.: process_access' : 'exige empresa ou agente'}
          maxLength={100}
          mono
        />
        <TextField
          label="Usuário"
          value={draft.userName}
          onValueChange={(v) => setDraft((d) => ({ ...d, userName: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'ator na máquina/empresa' : 'exige empresa ou agente'}
          mono
        />
        <SelectField
          label="Módulo"
          value={draft.eventModule}
          onValueChange={(v) => setDraft((d) => ({ ...d, eventModule: v }))}
          options={MODULE_OPTIONS}
          disabled={!hasScope}
          hint={hasScope ? 'origem do evento' : 'exige empresa ou agente'}
        />
        <TextField
          label="Nome do processo"
          value={draft.processName}
          onValueChange={(v) => setDraft((d) => ({ ...d, processName: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'ex.: powershell.exe' : 'exige empresa ou agente'}
          mono
        />
        <TextField
          label="Serviço"
          value={draft.serviceName}
          onValueChange={(v) => setDraft((d) => ({ ...d, serviceName: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'nome do serviço/unidade' : 'exige empresa ou agente'}
          mono
        />
        <TextField
          label="Extensão"
          value={draft.fileExtension}
          onValueChange={(v) => setDraft((d) => ({ ...d, fileExtension: v }))}
          disabled={!hasScope}
          hint={hasScope ? 'com ou sem ponto · ex.: exe' : 'exige empresa ou agente'}
          mono
        />
        <TextField
          label="Consulta DNS"
          value={draft.dnsQuestion}
          onValueChange={(v) => setDraft((d) => ({ ...d, dnsQuestion: v }))}
          hint="igualdade exata · pivô de frota, sem escopo"
          mono
        />
        <TextField
          label="Canal (source)"
          value={draft.source}
          onValueChange={(v) => setDraft((d) => ({ ...d, source: v }))}
          disabled={!hasEndpoint}
          hint={hasEndpoint ? 'ex.: Security' : 'exige um agente'}
          mono
        />
        <TextField
          label="native_id"
          value={draft.nativeId}
          onValueChange={(v) => setDraft((d) => ({ ...d, nativeId: v }))}
          disabled={!hasEndpoint}
          hint={hasEndpoint ? 'ex.: 4688' : 'exige um agente'}
          mono
        />
        <TextField
          label="Processo (GUID)"
          value={draft.processEntityId}
          onValueChange={(v) => setDraft((d) => ({ ...d, processEntityId: v }))}
          hint="timeline do processo · sem escopo"
          mono
        />
        <TextField
          label="IP relacionado"
          value={draft.relatedIp}
          onValueChange={(v) => setDraft((d) => ({ ...d, relatedIp: v }))}
          hint="fan-out por IP · sem escopo"
          mono
        />
        <TextField
          label="Usuário relacionado"
          value={draft.relatedUser}
          onValueChange={(v) => setDraft((d) => ({ ...d, relatedUser: v }))}
          hint="fan-out na frota · sem escopo"
          mono
        />
        <TextField
          label="Hash relacionado"
          value={draft.relatedHash}
          onValueChange={(v) => setDraft((d) => ({ ...d, relatedHash: v }))}
          hint="caça a IOC · sem escopo"
          mono
        />
        <TextField
          label="De (UTC)"
          value={draft.from}
          onValueChange={(v) => setDraft((d) => ({ ...d, from: v }))}
          disabled={!draftHasTimeScope}
          hint={draftHasTimeScope ? undefined : 'exige escopo, processo ou DNS'}
          type="datetime-local"
        />
        <TextField
          label="Até (UTC)"
          value={draft.to}
          onValueChange={(v) => setDraft((d) => ({ ...d, to: v }))}
          disabled={!draftHasTimeScope}
          hint={draftHasTimeScope ? undefined : 'exige escopo, processo ou DNS'}
          type="datetime-local"
        />
      </div>

      <div className="flex shrink-0 items-center gap-2 px-3 py-1.5">
        <Button variant="primary" size="sm" onClick={search} busy={loading}>
          Buscar
        </Button>
        {order !== '' && <span className="text-micro text-fg-muted">{rows.length} carregadas</span>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error !== null ? (
          <div className="p-3">
            <ErrorState
              error={error}
              onRetry={cursorInvalid ? () => setNonce((n) => n + 1) : undefined}
            />
            {cursorInvalid && (
              <p className="mt-2 text-sm text-fg-muted">A paginação expirou — recomece do início.</p>
            )}
          </div>
        ) : rows.length === 0 && loading ? (
          <LoadingRow label="Consultando o histórico…" />
        ) : rows.length === 0 ? (
          <EmptyState title="Nenhum evento no histórico para este filtro" />
        ) : (
          <>
            <div className="px-3 pt-2">
              <OrderAxisNote order={order} />
            </div>
            <LogTable rows={rows} />
            <div className="flex items-center justify-center gap-3 border-t border-line-subtle px-3 py-3">
              {hasMore ? (
                <Button onClick={loadOlder} busy={loading}>
                  Carregar mais antigos
                </Button>
              ) : (
                <span className="text-micro text-fg-muted">
                  Fim do histórico · {formatInt(rows.length)} eventos
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
